import type { Home, HomeGroup, IncidentReport } from '@lmc/ts-common'
import { loadImageAsCircularDataUrl } from 'components/pdf/images'
import useBodyMaps from 'hooks/useBodyMaps'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { step5Answers } from '../steps/injuriesAndDamages/appInjuries'
import { useServiceUserWords } from '../useServiceUserWords'
import {
  type InjuriesCase,
  injuryBodyMapIds,
  injuryEntries
} from '../view/readRules'
import { useServiceUserName, useTeamMemberName } from '../view/usePersonName'
import { loadCaseImages } from './caseImages'
import { buildIncidentCaseV2Document } from './incidentCaseV2Document'

/** The photo's side in the PDF, in points, as the design's avatar. */
const PHOTO_SIZE = 40

/**
 * The service user's photo, cropped to a circle as pdfmake can't. A missing
 * or unreadable photo prints the name alone rather than cost the download.
 */
const loadPhoto = async (picture: unknown) => {
  // Typed as `File` in ts-common, but the API presigns it to a URL string.
  if (typeof picture !== 'string' || !picture) {
    return undefined
  }
  try {
    return await loadImageAsCircularDataUrl(picture, PHOTO_SIZE)
  } catch (err) {
    // Never the url: it is signed and identifies whose photo this is.
    console.error('Failed to load service user photo', err)
    return undefined
  }
}

/**
 * Looks up everything a v2 case's document names (team members, service
 * users, the service user's record and linked body maps) and builds it once
 * they have loaded, fetching the service user's photo as it does. The same
 * queries as the case view, so on the case page they are already cached.
 */
const useIncidentCaseV2Document = (incidentReport: IncidentReport) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  // The signed-in user's group, which is the home's when they have one.
  const homeGroupName = useSelector(
    (state: { global: { homeGroup?: HomeGroup | null } }) =>
      state.global.homeGroup?.name
  )
  const serviceUserWords = useServiceUserWords()
  const serviceUsers = useServiceUserName()
  const teamMembers = useTeamMemberName()
  const resident = useResident({ id: incidentReport.serviceUser.id })
  const savedCase = (incidentReport.case ?? {}) as InjuriesCase
  const hasBodyMaps = injuryEntries(step5Answers(savedCase).injuries).some(
    ({ entry }) => injuryBodyMapIds(entry, savedCase).length > 0
  )
  // Only fetched when an injury links one, as the case view does.
  const bodyMaps = useBodyMaps({
    residentId: hasBodyMaps ? incidentReport.serviceUser.id : undefined
  })

  const buildDocument = async () => {
    const [photo, images] = await Promise.all([
      loadPhoto(resident.resident?.picture),
      loadCaseImages(incidentReport, home)
    ])
    return buildIncidentCaseV2Document(incidentReport, {
      bodyMaps: bodyMaps.bodyMaps,
      home,
      homeGroupName,
      images,
      photo,
      resident: resident.resident,
      serviceUserName: serviceUsers.nameOf,
      serviceUserWords,
      t,
      teamMemberName: teamMembers.nameOf
    })
  }

  return {
    buildDocument,
    // The resident's too: without it the details block would print empty.
    isError:
      bodyMaps.isError ||
      resident.isError ||
      serviceUsers.isError ||
      teamMembers.isError,
    isLoading:
      bodyMaps.isLoading ||
      resident.isLoading ||
      serviceUsers.isLoading ||
      teamMembers.isLoading
  }
}

export default useIncidentCaseV2Document
